<template>
  <div>
    <div>
      <label class="input-label">
        {{ `Servicios de ${service.disciplineName}` }}
      </label>
    </div>
    <div class="mb-4">
      <div v-for="(item, i) in compensatoryHourRequests" :key="i">
        <b-card
          :class="[
            `bg-light-gray border-left ${getBorderColor(item.statusId)}`,
            { 'mt-2': i > 0 },
          ]"
        >
          <b-row>
            <template v-if="canContinue && !item.documentId">
              <dl class="mt-1 ml-5 text-center">
                <CheckBox
                  v-model="item.selectedToCompensate"
                  class="font-weight-light text-md-nowrap"
                  :disabled="ReadOnly"
                />
              </dl>
            </template>
            <template>
              <b-col cols="12" md>
                <dl class="m-0 text-center">
                  <dd class="m-0">
                    <custom-balloon
                      :background-color="`bg-${getStatusColor(item.statusId)}`"
                      class="border-none text-center font-15"
                      :text="item.status"
                    />
                  </dd>
                  <dd class="m-0">{{ item.requestNumber }}</dd>
                </dl>
              </b-col>
            </template>
            <b-col class="mt-2 mt-md-0" cols="12" md>
              <dl class="m-0 text-md-center">
                <dd class="m-0 text-muted small">
                  Año Académico:
                </dd>
                <dd class="m-0">{{ item.academicYearDesc }}</dd>
              </dl>
            </b-col>
            <b-col class="mt-2 mt-md-0" cols="12" md>
              <dl class="m-0 text-md-center">
                <dd class="m-0 text-muted small">
                  Servicio:
                </dd>
                <dd class="m-0">{{ item.detail.disciplineName }}</dd>
              </dl>
            </b-col>
            <b-col class="mt-2 mt-md-0" cols="12" md>
              <dl class="m-0 text-md-center">
                <dd class="m-0 text-muted small">
                  Intervenciones Adeudadas:
                </dd>
                <dd class="m-0">{{ item.interventionsDue }}</dd>
              </dl>
            </b-col>
            <b-col class="mt-2 mt-md-0" cols="12" md>
              <dl class="m-0 text-md-center">
                <dd class="m-0 text-muted small">
                  Intervenciones Ofrecidas:
                </dd>
                <dd class="m-0">{{ item.interventionsOffered }}</dd>
              </dl>
            </b-col>
            <b-col class="mt-1 mt-md-0" cols="12" md>
              <dl class="m-0 text-md-center">
                <dd class="m-0 text-muted small">
                  Balance:
                </dd>
                <dd class="m-0">{{ item.interventionsDueTotal }}</dd>
              </dl>
            </b-col>
            <b-col class="mt-1 mt-md-0" cols="12" md>
              <div class="dropdown" v-if="canContinue && !ReadOnly">
                <button
                  id="dropdownMenuButton"
                  class="btn btn-dropdown-actions dropdown-toggle float-right pt-1 custom-dropdown-block"
                  type="button"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  Acciones
                </button>
                <div
                  class="dropdown-menu dropdown-menu-right"
                  aria-labelledby="dropdownMenuButton"
                >
                  <div class="dropdown-item" @click="modify(item)">
                    <em class="fa fa-pencil text-green pr-1" />
                    <span>Modificar</span>
                  </div>
                  <div class="dropdown-item" @click="openReject(item)">
                    <em class="fa fa-times-circle text-red pr-1" />
                    <span>Rechazar</span>
                  </div>
                </div>
              </div>
              <ActionButton
                v-if="canContinue === false && !ReadOnly"
                i="fas fa-times-circle"
                variant="outline-danger ml-5 mt-2 float-right pt-1 custom-dropdown-block"
                @click="openReject(item)"
              >
                Rechazar
              </ActionButton>
            </b-col>
          </b-row>
        </b-card>

        <RejectCompensatoryService
          :key="item.requestNumber"
          v-if="item.isOpen && !item.isModify"
          :compensatoryRequestNumber="item"
          :fileName="item.fileName"
          :documentAttachmentId="item.documentId"
          :isReadOnly="ReadOnly"
          @document="document"
          @save-doc="saveDocument()"
          @cancel-section="item.isOpen = false"
        />
        <ModifyCompensatoryService
          :key="item.requestNumber + 'modify'"
          v-if="item.isOpen && item.isModify && !isSigned"
          :originalQuantity="item.interventionsDue"
          :compensatoryRequestNumber="item.requestNumber"
          :quantity="item.preSaveInterventionAmount"
          :isReadOnly="ReadOnly"
          @change-quantity="changeOnQuantity"
          @save-quantity="saveQuantity"
          @cancel-modify="closeModify(item)"
        />
      </div>
      <CustomResultNotFound
        v-if="compensatoryHourRequests.length === 0"
        :text="`No posee registros de Intervenciones`"
      />
    </div>
  </div>
</template>

<script>
import Error from '@/components/common/Error';
import CustomRadioGroup from '@/components/common/CustomRadio';
import RejectCompensatoryService from './components/RejectCompensatoryService.vue';
import ModifyCompensatoryService from './components/ModifyCompensatoryService.vue';
import {
  getInterventions,
  updateInterventions,
  getInterventionSelection,
} from '@/modules/compensatory-service/api';
import CustomBalloon from 'custom-balloon';
import AdmissionStatus from '@/utils/constants/thrpiesAdmissionStatuses';
import CustomResultNotFound from 'custom-result-not-found';
import { SectionTitle } from '@/components/pei/shared';
import CheckBox from 'CheckBox';
import mixAlert from '@/mixin/alert.mixin';
import { ClosingAnalysis } from 'compensatory-service';
import {
  submitClosingAnalysis as _submitClosingAnalysis,
  getCompensatoryRequestAnalysisData as _getCompensatoryRequestAnalysisData,
  getCompensatoryRequestReplacementStrategies as _getCompensatoryRequestReplacementStrategies,
  getCompensatoryDocumentTypes as _getCompensatoryDocumentTypes,
  replacementDocumentUpload as _replacementDocumentUpload,
  deleteCompensatoryDocument as _deleteCompensatoryDocument,
  completeReplacement as _completeReplacement,
  resetReplacement as _resetReplacement,
  updateAmountWhenModifyCompensatory as _updateAmountWhenModifyCompensatory,
  saveAttachmentWhenRejectCompensatory as _saveAttachmentWhenRejectCompensatory,
  cleanCompensatoryService as _cleanCompensatoryService,
  getCompensatoryRequestReplacementCommentsStrategies as _getCompensatoryRequestReplacementCommentsStrategies,
} from '@/modules/compensatory-service/api';
import peiServices from '@/services/pei/general';
import store from '@/store/index';
import { ActionButton } from '@/components/common';
import peiStatusEnum from '@/utils/constants/pei/peiStatus.js';
import objectToFormData from 'object-to-formdata';
import compensatoryRequestStatus from '@/utils/constants/compensatoryRequestStatus.js';

export default {
  name: 'ServiceCompensationTabServiceRp',
  mixins: [mixAlert],
  components: {
    CustomRadioGroup,
    CustomBalloon,
    CustomResultNotFound,
    SectionTitle,
    CheckBox,
    ClosingAnalysis,
    RejectCompensatoryService,
    ModifyCompensatoryService,
    ActionButton,
    Error,
  },
  props: {
    sie: {
      default: 0,
    },
    peiId: {
      type: String,
      default: '',
    },
    disciplineTypeId: {
      default: 0,
    },
    programServiceAreaId: {
      default: 0,
    },
    isPeiComplete: {
      default: false,
    },
    canShowValidation: {
      default: false,
    },
    ReadOnly: {
      type: Boolean,
      default: false,
    },
    compensatoryHourRequests: {
      type: Array,
      default: [],
    },
    service: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      radioButtonsOptions: [
        { label: 'Si', value: true },
        { label: 'No', value: false },
      ],
      canContinue: null,
      interventionToSave: [],
      hasData: [],
      requestNumberList: [],
      validate: false,
      element: null,
      isModify: false,
      pendingElementsIntervention: [],
      originalCompensatoryHourRequests: [],
    };
  },
  computed: {
    hasCompensatoryHours() {
      return this.compensatoryHourRequests?.length === 0;
    },
    isSigned() {
      return store.getters.SelectedPeiStatus == Number(peiStatusEnum.Firmado);
    },
    msjValidation() {
      switch (this.canContinue) {
        case null:
          return 'Este campo debe ser completado.';
        case true:
          return 'Debe seleccionar al menos una planilla para compensar';
      }
    },
    showRequiredAlert() {
      if (this.ReadOnly && this.hasCompensatoryHours) return false;
      if (!this.ReadOnly) return true;
      return true;
    },
    validator() {
      return store.getters.tab11Validator;
    },
    hasError() {
      const hasCompletePeiError =
        !!this.programServiceAreaId &&
        this.validator.hasCompensationServiceInvalid(this.programServiceAreaId);

      return hasCompletePeiError;
    },
  },
  watch: {
    compensatoryHourRequests: {
      deep: true,
      async handler(newCompensatoryHourRequests) {
        const values = [...newCompensatoryHourRequests];
        const filteredData = values.map(
          ({
            selectedToCompensate,
            requestNumber,
            preSaveInterventionAmount,
            fileName,
            file,
          }) => ({
            compensatoryRequestNumber: requestNumber, //TODO: pending refact
            requestNumber, //TODO: pending refact
            selectedToCompensate,
            fileName,
            file,
            preSaveInterventionAmount,
            peiId: this.peiId,
          })
        );

        const changedSelection = filteredData.filter(
          (item, index) =>
            item.selectedToCompensate !==
              this.originalCompensatoryHourRequests[index]
                .selectedToCompensate ||
            (!!item.preSaveInterventionAmount &&
              item.preSaveInterventionAmount !==
                this.originalCompensatoryHourRequests[index]
                  .preSaveInterventionAmount) ||
            item.fileName !==
              this.originalCompensatoryHourRequests[index].fileName
        );

        changedSelection.forEach((item) => {
          if (!item.preSaveInterventionAmount) {
            delete item.preSaveInterventionAmount;
          }
        });

        this.interventionToSave = filteredData;
        this.pendingElementsIntervention = changedSelection;
        this.$emit('pending-changes', changedSelection.length > 0);
      },
    },
  },
  async mounted() {
    const selection = await getInterventionSelection(this.programServiceAreaId);
    this.canContinue = selection.data === '' ? null : selection.data;
    this.originalCompensatoryHourRequests = this.compensatoryHourRequests.map(
      ({
        selectedToCompensate,
        requestNumber,
        preSaveInterventionAmount,
        fileName,
      }) => ({
        requestNumber,
        compensatoryRequestNumber: requestNumber,
        selectedToCompensate,
        fileName,
        preSaveInterventionAmount,
        peiId: this.peiId,
      })
    );

    await this.checkDifferentPeiId();
    await this.validateFromSignPei();
  },
  methods: {
    async save() {
      let isValid = true;

      isValid = isValid && (await this.innerSave());

      if (!!this.pendingElementsIntervention.length) {
        const pendigPromises = [];
        this.pendingElementsIntervention
          .filter(
            (element) =>
              !!element.preSaveInterventionAmount || !!element.fileName
          )
          .forEach((element) => {
            const payload = {
              compensatoryRequestNumber: element.compensatoryRequestNumber,
              preSaveInterventionAmount: parseInt(
                element.preSaveInterventionAmount
              ),
            };
            if (element?.preSaveInterventionAmount) {
              pendigPromises.push(this.saveQuantity(payload));
            }
            if (this.element != null) {
              this.saveDocument(element);
            }
          });

        await Promise.all(pendigPromises);

        await this.loadData();
        this.pendingElementsIntervention = [];
        this.element = null;
      }

      return isValid;
    },
    async innerSave() {
      const isValid = await this.$refs.refValidation.validate();
      if (!isValid) {
        this.ShowToast(
          'Error',
          'Favor de completar la información solicitada.',
          'error'
        );

        return isValid;
      }

      let dataToSend = {
        valueQuestion: this.canContinue,
        programServiceAreaId: parseInt(this.programServiceAreaId),
        isFromQuestion: false,
        interventionToCompensate: this.interventionToSave,
      };
      await updateInterventions(dataToSend);
      this.ShowToast(
        'Completado',
        'Su información ha sido guardada.',
        'success'
      );
      this.$emit('pending-changes', false);
      this.validate = false;

      return isValid;
    },
    changeOnQuantity(e) {
      this.compensatoryHourRequests.find(
        (x) => x.requestNumber == e.compensatoryRequestNumber
      ).preSaveInterventionAmount = e.quantity;
    },
    async validateFromSignPei() {
      const listTovalidate = store.state.compensatoryServicesValidation;
      if (listTovalidate.length < 1 || listTovalidate == null) {
        return;
      }
      const isValid = await this.$refs.refValidation.validate();
      this.validate = true;
      return isValid;
    },
    async checkDifferentPeiId() {
      const hasDifferentPeiId = this.compensatoryHourRequests.some(
        (item) => item.peiId !== this.peiId
      );
      const hasNullPeiId = this.compensatoryHourRequests.some(
        (item) => item.peiId == null
      );
      if (hasDifferentPeiId && !hasNullPeiId) {
        this.interventionToSave = this.compensatoryHourRequests.map(
          ({ selectedToCompensate, requestNumber }) => ({
            selectedToCompensate,
            requestNumber,
            peiId: this.peiId,
          })
        );
        let dataToSend = {
          valueQuestion: this.canContinue,
          programServiceAreaId: parseInt(this.programServiceAreaId),
          isFromQuestion: true,
          interventionToCompensate: this.interventionToSave,
        };
        await updateInterventions(dataToSend);
        this.ShowToast(
          'Completado',
          'Su información ha sido guardada.',
          'success'
        );
      }
    },
    async loadData() {
      //Save a copy to evaluate pending changes
      this.$emit('load-data', true);
    },
    modify(item) {
      item.isOpen = true;
      item.isModify = true;
    },
    closeModify(item) {
      item.isOpen = false;
      item.isModify = false;
    },
    openReject(item) {
      item.isOpen = true;
      item.isModify = false;
    },
    async saveQuantity(item) {
      await _updateAmountWhenModifyCompensatory(item);
      await this.loadData();
    },
    async cleanCompensatoryService() {
      let dataToSendToupdateIntervention = {
        valueQuestion: this.canContinue,
        programServiceAreaId: parseInt(this.programServiceAreaId),
        isFromQuestion: true,
        interventionToCompensate: this.interventionToSave,
      };
      let dataToSendClean = {
        compensatoryRequestNumberList: this.requestNumberList,
      };
      if (this.canContinue === '' || this.canContinue === null) {
        return;
      }

      await updateInterventions(dataToSendToupdateIntervention);
      this.ShowToast(
        'Completado',
        'Su información ha sido guardada.',
        'success'
      );
      await _cleanCompensatoryService(dataToSendClean);
      await this.loadData();
    },
    getStatusColor(status) {
      switch (status) {
        case compensatoryRequestStatus.ProcessCompleted:
        case compensatoryRequestStatus.Approved:
        case compensatoryRequestStatus.ApprovedModified:
          return 'green-light';
        case compensatoryRequestStatus.CompletedPendingApproval:
        case compensatoryRequestStatus.Submitted:
          return 'yellow';
        case compensatoryRequestStatus.New:
          return 'gray';
        case compensatoryRequestStatus.Denied:
        case compensatoryRequestStatus.RequestRejected:
        case compensatoryRequestStatus.ExcludedByStrategy:
        case compensatoryRequestStatus.ExcludedGraduated:
        case compensatoryRequestStatus.ExcludedServiceHigh:
          return 'brown';
        case compensatoryRequestStatus.ProcessDiscussion:
          return 'violet';
        default:
          return 'green';
      }
    },
    getBorderColor(status) {
      switch (status) {
        case compensatoryRequestStatus.ProcessCompleted:
        case compensatoryRequestStatus.Approved:
        case compensatoryRequestStatus.ApprovedModified:
          return 'border-left--green-light';
        case compensatoryRequestStatus.CompletedPendingApproval:
        case compensatoryRequestStatus.Submitted:
          return 'border-left--yellow';
        case compensatoryRequestStatus.New:
          return 'border-left--gray';
        case compensatoryRequestStatus.Denied:
        case compensatoryRequestStatus.RequestRejected:
        case compensatoryRequestStatus.ExcludedByStrategy:
        case compensatoryRequestStatus.ExcludedGraduated:
        case compensatoryRequestStatus.ExcludedServiceHigh:
          return 'border-left--brown';
        case compensatoryRequestStatus.ProcessDiscussion:
          return 'border-left--violet';
        default:
          return 'border-left--reen';
      }
    },
    submitClosingAnalysis(id) {
      return _submitClosingAnalysis(id);
    },
    getCompensatoryRequestAnalysisData(id) {
      return _getCompensatoryRequestAnalysisData(id);
    },
    getCompensatoryRequestReplacementStrategies(replacementId) {
      return _getCompensatoryRequestReplacementStrategies(replacementId);
    },
    getCompensatoryDocumentTypes(requestId) {
      return _getCompensatoryDocumentTypes(requestId);
    },
    replacementDocumentUpload(params) {
      return _replacementDocumentUpload(params);
    },
    deleteCompensatoryDocument(params) {
      return _deleteCompensatoryDocument(params);
    },
    completeReplacement(payload) {
      this.validate = false;
      return _completeReplacement(payload);
    },
    resetReplacement(replacementId) {
      return _resetReplacement(replacementId);
    },
    downloadDocumentByAttachmentId(documentId) {
      return peiServices.downloadDocumentByAttachmentId(documentId);
    },
    getCompensatoryRequestReplacementCommentsStrategies(replacementId) {
      return _getCompensatoryRequestReplacementCommentsStrategies(
        replacementId
      );
    },
    async saveDocument(item = null) {
      if (item) {
        const formData = objectToFormData(item, { indices: true });
        formData.append(`document`, item.file);
        formData.append(`compensatoryRequestNumber`, item.requestNumber);
        await _saveAttachmentWhenRejectCompensatory(formData);
        await this.loadData();
        return;
      }
      const formData = objectToFormData(this.element, { indices: true });
      formData.append(`document`, this.element.file);
      formData.append(
        `compensatoryRequestNumber`,
        this.element.compensatoryRequestNumber.requestNumber
      );
      await _saveAttachmentWhenRejectCompensatory(formData);
      await this.loadData();
    },
    document(doc) {
      this.compensatoryHourRequests.find(
        (x) => x.requestNumber == doc.compensatoryRequestNumber.requestNumber
      ).fileName = doc.file.name;
      this.compensatoryHourRequests.find(
        (x) => x.requestNumber == doc.compensatoryRequestNumber.requestNumber
      ).file = doc.file;
      this.element = doc;
    },
  },
};
</script>

<style scoped>
.container-analysis-wrapper {
  margin-top: 10px;
  background-color: #f7f7f7;
  padding: 5px;
  border-radius: 5px;
}
.container-analysis {
  background-color: white;
  padding: 5px;
  border-radius: 5px;
}
.border-left {
  border: 0 !important;
  border-left-style: solid !important;
  border-left-width: 5px !important;
}

.border-left--primary-13 {
  border-left-color: #f8ca80 !important;
}

.border-left--green-light {
  border-left-color: #a3e5b3 !important;
}

.border-left--yellow {
  border-left-color: #f8ea80 !important;
}

.border-left--green {
  border-left-color: #2db782 !important;
}

.border-left--brown {
  border-left-color: #d5b89a !important;
}

.border-left--gray {
  border-left-color: #d0d0d0 !important;
}

.border-left--violet {
  border-left-color: #ddb3d7 !important;
}
</style>
